'use client';

import { useQuery } from '@tanstack/react-query';

import { unwrapLambdaResponse } from '@/lib/api';
import { useAccessToken } from '@/lib/auth-client';
import { BACKEND_URL } from '@/lib/constants';

export interface DemoSpaceStatus {
  /** False when demo seeding is off in this environment. */
  enabled: boolean;
  spaceId: string | null;
  /** Server truth for whether each demo replay ran to completion. */
  completed: { sheet: boolean; deck: boolean; comments: boolean };
  /** Walkthrough currently mid-run, if any. */
  activeReplay: 'sheet' | 'deck' | 'comments' | null;
}

export const DEMO_STATUS_QUERY_KEY = ['demo-space-status'] as const;

export function useDemoSpaceStatus(options?: { enabled?: boolean }) {
  const { getAccessToken, loading: authLoading } = useAccessToken();

  return useQuery({
    queryKey: DEMO_STATUS_QUERY_KEY,
    queryFn: async (): Promise<DemoSpaceStatus> => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`${BACKEND_URL}/spaces/demo/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Demo status request failed (${response.status})`);
      }
      return unwrapLambdaResponse<DemoSpaceStatus>(await response.json());
    },
    enabled: !authLoading && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}
